import { createContext, useContext, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Bot, Brain, CheckCircle2, ChevronDown, Database, FileText, Gavel, Loader2, Paperclip, RefreshCw, Search, ShieldAlert, Sparkles, Undo2, Workflow, Wrench, XCircle, type LucideIcon } from "lucide-react";
import { citationMarkers } from "../lib/citations";
import type { InlineWidgetRef } from "../lib/inlineWidgets";
import { cn } from "../lib/cn";
import { microcopy, useDocumentLocale } from "../lib/microcopy";
import { Badge } from "./badge";
import { Button } from "./button";
import { CitationList, type CitationLike } from "./citations";
import { MarkdownBody } from "./markdown";
import { Conversation, ConversationContent, ConversationScrollButton } from "./chat/conversation";
import { Message, MessageContent, MessageHeader } from "./chat/message";
import { PromptInput, PromptInputAttachment, PromptInputButton, PromptInputSubmit, PromptInputTextarea, PromptInputToolbar, PromptInputTools } from "./chat/prompt-input";
import { Sources } from "./chat/sources";
import { Suggestion, Suggestions } from "./chat/suggestions";
import { Shimmer } from "./chat/work-steps";
import { SourceText } from "./data-display";
import { StreamingCaret } from "./motion";

export { CitationChip, CitationHoverCard, CitationList, CitationMarker, CitationPreview, SourceDrawer, citationDate, citationDates, citationIcon, citationKind, citationUrl, type CitationLike } from "./citations";

export { MarkdownBody, type MarkdownComponents } from "./markdown";

/**
 * Keeps a feed pinned to its newest entry by scrolling only its own scroll container. `scrollIntoView` would also
 * scroll every ancestor, including the window, which yanked whole pages down whenever a panel mounted or streamed.
 */
function scrollFeedToEnd(marker: HTMLElement | null, behavior: ScrollBehavior = "smooth"): void {
  for (let el = marker?.parentElement; el && el !== document.body; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY === "auto" || overflowY === "scroll") {
      el.scrollTo({ top: el.scrollHeight, behavior });
      return;
    }
  }
}

// ---------------------------------------------------------------- Agent activity feed
export interface AgentStepLike {
  id: string;
  sequence: number;
  kind: "Started" | "Thought" | "Retrieval" | "ToolCall" | "ToolResult" | "Draft" | "Guardrail" | "Token" | "Completed" | "Failed";
  title: string;
  titleAr?: string | null;
  detail?: string | null;
  occurredAt: string;
  durationMs?: number | null;
}

function formatDuration(ms: number, locale: "en" | "ar"): string {
  if (locale === "ar") return new Intl.NumberFormat("ar-AE-u-nu-latn", { style: "unit", unit: "second", unitDisplay: "long", maximumFractionDigits: 1 }).format(ms / 1000);
  if (ms < 1000) return new Intl.NumberFormat("en-AE", { style: "unit", unit: "millisecond", unitDisplay: "short" }).format(ms);
  return new Intl.NumberFormat("en-AE", { style: "unit", unit: "second", unitDisplay: "short", maximumFractionDigits: 1 }).format(ms / 1000);
}

const stepMeta: Record<AgentStepLike["kind"], { icon: LucideIcon; tone: string }> = {
  Started: { icon: Sparkles, tone: "text-aegold-700 bg-aegold-100" },
  Thought: { icon: Brain, tone: "text-seablue-700 bg-seablue-100" },
  Retrieval: { icon: Search, tone: "text-camel-800 bg-camel-100" },
  ToolCall: { icon: Wrench, tone: "text-aeblack-800 bg-sand-200" },
  ToolResult: { icon: Database, tone: "text-aeblack-800 bg-sand-200" },
  Draft: { icon: FileText, tone: "text-aegold-800 bg-aegold-100" },
  Guardrail: { icon: ShieldAlert, tone: "text-warning-700 bg-warning-100" },
  Token: { icon: Bot, tone: "text-sand-700 bg-sand-100" },
  Completed: { icon: CheckCircle2, tone: "text-success-700 bg-success-100" },
  Failed: { icon: XCircle, tone: "text-danger-700 bg-danger-100" },
};

export function AgentActivityFeed({ steps, running, className, compact = false }: { steps: AgentStepLike[]; running?: boolean; className?: string; compact?: boolean }) {
  const endRef = useRef<HTMLDivElement>(null);
  const locale = useDocumentLocale();
  useEffect(() => {
    scrollFeedToEnd(endRef.current);
  }, [steps.length]);
  const visible = steps.filter((s) => s.kind !== "Token");
  return (
    <div className={cn("relative flex flex-col gap-2", className)}>
      <AnimatePresence initial={false}>
        {visible.map((s) => {
          const meta = stepMeta[s.kind];
          return (
            <motion.div key={s.id} layout initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className={cn("flex items-start gap-3 rounded-xl border border-sand-200/80 bg-white/70 p-2.5", compact && "p-2")}>
              <span className={cn("mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg", meta.tone)}>
                <meta.icon className="size-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-medium leading-tight">{locale === "ar" ? (s.titleAr ?? s.title) : s.title}</p>
                  {s.durationMs ? <span className="text-[11px] text-muted-foreground tabular" dir="auto">{formatDuration(s.durationMs, locale)}</span> : null}
                </div>
                {s.detail && !compact && (locale !== "ar" || s.titleAr == null) ? <p className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground" dir="auto">{citationMarkers(s.detail, locale)}</p> : null}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      {running ? (
        <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin text-aegold-700" />
          {microcopy("working", locale)}
        </div>
      ) : null}
      <div ref={endRef} />
    </div>
  );
}

// ---------------------------------------------------------------- Proposal card
export interface ProposalLike {
  id: string;
  agentName: string;
  agentNameAr?: string | null;
  kind: string;
  title: string;
  titleAr?: string | null;
  summary: string;
  summaryAr?: string | null;
  contentMarkdown: string;
  contentMarkdownAr?: string | null;
  /** The Arabic body is the machine translation of the approved English text, not part of the approved document. */
  arabicBodyTranslated?: boolean;
  confidence: number;
  status: string;
  citations: CitationLike[];
  guardrailFlags: string[];
  createdAt: string;
  caseNumber?: string | null;
  proposedAction?: { type: string; label: string; labelAr?: string | null } | null;
  modelId?: string;
  qualityScore?: number | null;
  qualityNotes?: string | null;
  /** Workflow step the proposal was produced for; rendered as a step tag when `formatStep` resolves it. */
  workflowState?: string | null;
}

/** Score badge of the independent AI quality review, with its issues as the tooltip. */
export function QualityBadge({ score, notes, label }: { score: number; notes?: string | null; label?: string }) {
  const tone = score >= 80 ? "success" : score >= 60 ? "warning" : "danger";
  return (
    <Badge variant={tone} size="sm" title={[microcopy("qualityDefinition"), notes].filter(Boolean).join("\n")}>
      <Sparkles /> {label ?? microcopy("quality")} {score}/100
    </Badge>
  );
}

/**
 * The scale an agent draft's confidence is read on, as the app's published method states it (condition 2.16): the percentages from which it
 * reads high and medium, and the ⓘ that explains the measure. Without a provider the meter keeps its built-in bands and its hover text.
 */
export interface ConfidenceScale { highFrom: number; mediumFrom: number; info?: ReactNode }
const ConfidenceScaleContext = createContext<ConfidenceScale | null>(null);

export function ConfidenceScaleProvider({ value, children }: { value: ConfidenceScale | null; children: ReactNode }) {
  return <ConfidenceScaleContext.Provider value={value}>{children}</ConfidenceScaleContext.Provider>;
}

/**
 * `labelled={false}` in a table column that already says "Confidence": the bar and the figure, without the word on every row. `published`:
 * an agent draft's confidence, read on the bands of the published method with its ⓘ when the app provides them.
 */
export function ConfidenceMeter({ value, className, labelled = true, published = false }: { value: number; className?: string; labelled?: boolean; published?: boolean }) {
  const scale = useContext(ConfidenceScaleContext);
  const bands = published && scale ? scale : { highFrom: 80, mediumFrom: 60 };
  const pct = Math.round(value * 100);
  const tone = pct >= bands.highFrom ? "bg-success-500" : pct >= bands.mediumFrom ? "bg-warning-500" : "bg-danger-500";
  return (
    <div className={cn("flex items-center gap-2", className)} title={published && scale?.info ? undefined : microcopy("confidenceDefinition")}>
      {labelled ? <span className="text-xs text-muted-foreground">{microcopy("confidence")}</span> : null}
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-sand-200">
        <motion.div className={cn("h-full rounded-full", tone)} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
      </div>
      <span className="text-xs tabular text-muted-foreground">{pct}%</span>
      {published && scale?.info ? scale.info : null}
    </div>
  );
}

/** Agents often return a one-paragraph Arabic abstract in `contentMarkdownAr`; only a rendering of comparable length counts as the Arabic body. */
export function hasArabicBody(p: Pick<ProposalLike, "contentMarkdown" | "contentMarkdownAr">): boolean {
  return !!p.contentMarkdownAr && p.contentMarkdownAr.length >= p.contentMarkdown.length * 0.45;
}

export function ProposalCard({ proposal, onApprove, onEdit, onReject, onRevise, onOpenCitation, onToggle, onReadToEnd, labels, className, children, defaultOpen = false, formatKind, formatFlag, formatStatus, formatStep }: {
  proposal: ProposalLike;
  onApprove?: (p: ProposalLike) => void;
  onEdit?: (p: ProposalLike) => void;
  onReject?: (p: ProposalLike) => void;
  /** Ask the agent to redraft with a note (human-in-the-loop revision). */
  onRevise?: (p: ProposalLike) => void;
  onOpenCitation?: (c: CitationLike) => void;
  /** Fired when the reviewer expands or collapses the draft (used to fetch the Arabic rendering lazily). */
  onToggle?: (open: boolean, p: ProposalLike) => void;
  /** Fired once, when the end of the draft's full text has been on screen: the reading its approval rests on (Condition 3.10). */
  onReadToEnd?: (p: ProposalLike) => void;
  labels: { approve: string; edit: string; reject: string; proposedAction: string; humanInLoop: string; confidence: string; revise?: string };
  /** Localised labels for the proposal kind, guardrail flags and status (raw identifiers are shown when omitted). */
  formatKind?: (kind: string) => string;
  formatFlag?: (flag: string) => string;
  formatStatus?: (status: string) => string;
  /** Resolves a workflow state key to its display label; return null/undefined to hide the step tag. */
  formatStep?: (state: string) => string | null | undefined;
  className?: string;
  children?: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const pending = proposal.status === "Proposed";
  // The opening is recorded when the officer has the end of the full text on screen, not when the card unfolds (Condition 3.10).
  const endRef = useRef<HTMLDivElement>(null);
  const readToEnd = useRef(onReadToEnd);
  readToEnd.current = onReadToEnd;
  useEffect(() => {
    const end = endRef.current;
    if (!open || !pending || !end || !readToEnd.current) return;
    if (typeof IntersectionObserver === "undefined") { readToEnd.current(proposal); return; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { readToEnd.current?.(proposal); observer.disconnect(); }
    });
    observer.observe(end);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pending, proposal.id]);
  const ar = useDocumentLocale() === "ar";
  // Older Arabic titles end on a separator where a case number was left out.
  const title = ar && proposal.titleAr ? proposal.titleAr.replace(/\s*[–-]\s*$/, "") : proposal.title;
  // An Arabic reader is not shown a summary the agent wrote only in English: the Arabic title stands alone.
  const summaryText = ar ? proposal.summaryAr ?? (/[A-Za-z]{3,}/.test(proposal.summary) ? "" : proposal.summary) : proposal.summary;
  const summary = summaryText ? citationMarkers(summaryText, ar ? "ar" : "en") : null;
  const body = ar && hasArabicBody(proposal) ? proposal.contentMarkdownAr! : proposal.contentMarkdown;
  const agentName = ar && proposal.agentNameAr ? proposal.agentNameAr : proposal.agentName;
  const actionLabel = proposal.proposedAction ? (ar && proposal.proposedAction.labelAr ? proposal.proposedAction.labelAr : proposal.proposedAction.label) : null;
  const stepLabel = proposal.workflowState && formatStep ? formatStep(proposal.workflowState) : null;
  return (
    <motion.article layout className={cn("surface overflow-hidden", pending && "ring-1 ring-aegold-200", className)}>
      <div className="flex items-start gap-3 p-4">
        <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-aegold-100 to-seablue-100 text-aegold-800">
          <Bot className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="ai" size="sm">{agentName}</Badge>
            <Badge variant="outline" size="sm">{formatKind ? formatKind(proposal.kind) : proposal.kind}</Badge>
            {stepLabel ? <Badge variant="gold" size="sm" title={proposal.workflowState ?? undefined}><Workflow /> {stepLabel}</Badge> : null}
            {proposal.caseNumber ? <span className="text-xs text-muted-foreground tabular">{proposal.caseNumber}</span> : null}
            {proposal.guardrailFlags.map((f) => (
              <Badge key={f} variant="warning" size="sm"><ShieldAlert /> {formatFlag ? formatFlag(f) : f}</Badge>
            ))}
            <span className="ms-auto flex items-center gap-2">
              {proposal.qualityScore !== undefined && proposal.qualityScore !== null ? <QualityBadge score={proposal.qualityScore} notes={proposal.qualityNotes} /> : null}
              <ConfidenceMeter value={proposal.confidence} published />
            </span>
          </div>
          <h3 className="mt-1.5 text-base font-semibold leading-tight">{ar ? <SourceText text={title} /> : title}</h3>
          {summary ? <p className="mt-1 text-sm text-muted-foreground">{ar ? <SourceText text={summary} /> : summary}</p> : null}
          {proposal.proposedAction ? (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-sand-100 px-2 py-1 text-xs text-aeblack-900">
              <span className="font-semibold uppercase tracking-wider text-sand-600">{labels.proposedAction}:</span> {actionLabel}
            </p>
          ) : null}
        </div>
      </div>
      <button type="button" onClick={() => setOpen((o) => { onToggle?.(!o, proposal); return !o; })} className="flex w-full items-center justify-between border-t border-sand-200 bg-sand-50/60 px-4 py-2 text-xs font-medium text-aegold-800 hover:bg-sand-100">
        <span>{open ? microcopy("hideDraft") : microcopy("showDraft")}</span>
        <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <div className="border-t border-sand-200 p-4">
              {ar && proposal.arabicBodyTranslated && body === proposal.contentMarkdownAr ? <p className="mb-2 rounded-lg bg-sand-50 px-3 py-1.5 text-xs text-sand-700">{microcopy("approvedTranslation")}</p> : null}
              <MarkdownBody className="prose-sm" citations={proposal.citations} onOpenCitation={onOpenCitation}>{body}</MarkdownBody>
              <div ref={endRef} aria-hidden className="h-px" />
              <CitationList citations={proposal.citations} onOpen={onOpenCitation} className="mt-4" />
              {children}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
      {pending ? (
        <div className="border-t border-sand-200 bg-white p-3">
          <p className="mb-2 text-[11px] text-muted-foreground">{labels.humanInLoop}</p>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => onReject?.(proposal)} className="text-danger-700 hover:bg-danger-50"><XCircle /> {labels.reject}</Button>
            {onRevise && labels.revise ? <Button size="sm" variant="ghost" onClick={() => onRevise(proposal)}><RefreshCw /> {labels.revise}</Button> : null}
            <Button size="sm" variant="outline" onClick={() => onEdit?.(proposal)}>{labels.edit}</Button>
            <Button size="sm" variant="gold" onClick={() => onApprove?.(proposal)}><CheckCircle2 /> {labels.approve}</Button>
          </div>
        </div>
      ) : (
        <div className="border-t border-sand-200 bg-white px-4 py-2 text-xs text-muted-foreground">
          <Badge variant={proposal.status === "Rejected" ? "danger" : "success"} size="sm" dot>{formatStatus ? formatStatus(proposal.status) : proposal.status}</Badge>
        </div>
      )}
    </motion.article>
  );
}

// ---------------------------------------------------------------- Chat panel
/** A field-level suggestion the assistant derived from an uploaded document; the user accepts it with one click. */
export interface ChatSuggestionLike {
  id: string;
  label: string;
  value: string;
  detail?: string | null;
  confidence?: number | null;
  applied?: boolean;
}

export interface ChatMessageLike {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  citations?: CitationLike[];
  confidence?: number | null;
  streaming?: boolean;
  escalatable?: boolean;
  /** Attachments the user sent with this message (rendered as chips on the user bubble). */
  attachments?: Array<{ name: string; status?: "uploading" | "uploaded" | "failed" }>;
  /** Pre-fill suggestions offered by the assistant (rendered as accept chips under the answer). */
  suggestions?: ChatSuggestionLike[];
  /** Overrides the panel's `assistantName` label for this answer (e.g. a delegated specialist agent). */
  author?: string;
  /** Rendered above the answer's text (the work the assistant did, status while streaming). */
  before?: ReactNode;
  /** Rendered below the answer's text and sources (proposal cards, files, message actions). */
  after?: ReactNode;
  /** Renders the entry full-width without the assistant's name (for cards that stand on their own). */
  bare?: boolean;
  /** Draws a widget the text places with a marker (`[[w:<id>]]` alone on its line); without it markers draw nothing. */
  renderWidget?: (ref: InlineWidgetRef) => ReactNode;
  /** The entry's React key when it differs from `id` (an answer keeps one key from its first token to its saved form). */
  key?: string;
}

export interface ChatPanelLabels {
  send: string;
  escalate: string;
  thinking: string;
  attach?: string;
  apply?: string;
  applied?: string;
  undo?: string;
  stop?: string;
}

/**
 * A conversation: the feed follows new content while it streams, the user's words sit in soft bubbles and the assistant's answers read
 * as plain text with their sources folded underneath, and the composer is one card with its tools under the text.
 */
export function ChatPanel({ messages, onSend, onOpenCitation, onEscalate, onAttach, onApplySuggestion, onUndoSuggestion, onStop, accept, placeholder, disclaimer, busy, className, suggestions, labels, assistantName, assistantIcon, header, footer, composerTop, emptyState, composerStart, composerEnd, inputRef, bodyClassName, draft, onDraftChange, onComposerKeyDown }: {
  messages: ChatMessageLike[];
  onSend: (text: string) => void;
  onOpenCitation?: (c: CitationLike) => void;
  onEscalate?: () => void;
  /** Enables the paperclip, drag and drop and paste: files picked by the user are handed back for upload. */
  onAttach?: (files: File[]) => void;
  /** Called when the user accepts one of an assistant message's suggestions. */
  onApplySuggestion?: (messageId: string, suggestion: ChatSuggestionLike) => void;
  /** Called when the user undoes a suggestion that was applied automatically (dictation mode). */
  onUndoSuggestion?: (messageId: string, suggestion: ChatSuggestionLike) => void;
  /** Stops the answer being written; while `busy`, the send button becomes a stop button. */
  onStop?: () => void;
  /** Accepted MIME types / extensions for the attachment picker. */
  accept?: string;
  placeholder: string;
  disclaimer?: ReactNode;
  busy?: boolean;
  className?: string;
  suggestions?: string[];
  labels: ChatPanelLabels;
  assistantName: string;
  /** The assistant's mark beside its name (a spark by default). */
  assistantIcon?: ReactNode;
  header?: ReactNode;
  /** Rendered above the composer (e.g. a completion menu, or the next question the assistant is waiting for). */
  footer?: ReactNode;
  /** Rendered inside the composer, above the text (files waiting to go with the next message). */
  composerTop?: ReactNode;
  /** Shown in the feed while there are no messages (greeting, ways to start). */
  emptyState?: ReactNode;
  /** Extra composer tools after the attach button (voice, start a process). */
  composerStart?: ReactNode;
  /** Extra composer controls before the send button. */
  composerEnd?: ReactNode;
  /** Lets the host focus the textarea (keyboard shortcuts) or read the draft. */
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  bodyClassName?: string;
  /** Controlled draft (for @mention / slash-command completion in the host); leave undefined for an internal draft. */
  draft?: string;
  onDraftChange?: (text: string) => void;
  /** Intercepts composer key presses (e.g. to drive a completion menu). Return true to swallow the key. */
  onComposerKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => boolean | void;
}) {
  const locale = useDocumentLocale();
  const [localText, setLocalText] = useState("");
  const controlled = draft !== undefined;
  const text = controlled ? draft : localText;
  const setText = (v: string) => { if (controlled) onDraftChange?.(v); else setLocalText(v); };
  const fileRef = useRef<HTMLInputElement>(null);
  const localInputRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef ?? localInputRef;

  const submit = () => {
    const t = text.trim();
    if (!t || busy) return;
    onSend(t);
    setText("");
  };

  const showSuggestions = useMemo(() => messages.filter((m) => m.role === "user").length === 0 && (suggestions?.length ?? 0) > 0, [messages, suggestions]);
  const last = messages[messages.length - 1];
  const attachLabel = labels.attach ?? microcopy("attach", locale);

  return (
    <div className={cn("flex h-full min-h-0 flex-col overflow-hidden surface", className)}>
      {header ? <div className="border-b border-sand-200 px-4 py-3">{header}</div> : null}
      <Conversation>
        <ConversationContent className={cn("mx-auto w-full max-w-3xl space-y-6 px-4 py-6", bodyClassName)}>
          {messages.length === 0 && emptyState ? emptyState : null}
          {messages.map((m) => (
            <ChatEntry
              key={m.key ?? m.id}
              message={m}
              assistantName={assistantName}
              assistantIcon={assistantIcon}
              labels={labels}
              onOpenCitation={onOpenCitation}
              onEscalate={onEscalate}
              onApplySuggestion={onApplySuggestion}
              onUndoSuggestion={onUndoSuggestion}
            />
          ))}
          {busy && !last?.streaming ? (
            <div className="flex items-center gap-2 text-xs">
              <Loader2 className="size-3.5 animate-spin text-aegold-700" /> <Shimmer>{labels.thinking}</Shimmer>
            </div>
          ) : null}
          {showSuggestions ? (
            <Suggestions className="pt-1">
              {suggestions!.map((s) => <Suggestion key={s} onClick={() => onSend(s)}>{s}</Suggestion>)}
            </Suggestions>
          ) : null}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>
      <div className="px-3 pb-3 pt-1 md:px-4">
        <div className="mx-auto w-full max-w-3xl">
          {footer ? <div className="mb-2">{footer}</div> : null}
          <PromptInput onSubmit={submit} onFiles={onAttach && !busy ? onAttach : undefined}>
            {composerTop}
            <PromptInputTextarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (onComposerKeyDown?.(e)) e.preventDefault(); }}
              onSubmitKey={submit}
              placeholder={placeholder}
              aria-label={placeholder}
            />
            <PromptInputToolbar>
              <PromptInputTools>
                {onAttach ? (
                  <>
                    <input ref={fileRef} type="file" multiple accept={accept} className="hidden" onChange={(e) => { const files = Array.from(e.target.files ?? []); if (files.length) onAttach(files); e.target.value = ""; }} />
                    <PromptInputButton tooltip={attachLabel} aria-label={attachLabel} disabled={busy} onClick={() => fileRef.current?.click()}><Paperclip /></PromptInputButton>
                  </>
                ) : null}
                {composerStart}
              </PromptInputTools>
              {composerEnd}
              <PromptInputSubmit streaming={!!busy} onStop={onStop} disabled={busy || !text.trim()} label={labels.send} stopLabel={labels.stop} />
            </PromptInputToolbar>
          </PromptInput>
          {disclaimer ? <p className="mt-2 text-center text-[11px] leading-snug text-muted-foreground">{disclaimer}</p> : null}
        </div>
      </div>
    </div>
  );
}

function ChatEntry({ message: m, assistantName, assistantIcon, labels, onOpenCitation, onEscalate, onApplySuggestion, onUndoSuggestion }: {
  message: ChatMessageLike;
  assistantName: string;
  assistantIcon?: ReactNode;
  labels: ChatPanelLabels;
  onOpenCitation?: (c: CitationLike) => void;
  onEscalate?: () => void;
  onApplySuggestion?: (messageId: string, suggestion: ChatSuggestionLike) => void;
  onUndoSuggestion?: (messageId: string, suggestion: ChatSuggestionLike) => void;
}) {
  const locale = useDocumentLocale();
  if (m.role === "user") {
    return (
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        <Message from="user">
          <MessageContent from="user">
            {m.content ? <p className="whitespace-pre-wrap" dir="auto">{citationMarkers(m.content)}</p> : null}
            {m.attachments && m.attachments.length > 0 ? (
              <div className={cn("flex flex-wrap gap-1.5", m.content && "mt-2")}>
                {m.attachments.map((a) => <PromptInputAttachment key={a.name} name={a.name} status={a.status} className="bg-white/80" />)}
              </div>
            ) : null}
          </MessageContent>
        </Message>
      </motion.div>
    );
  }
  const body = (
    <>
      {m.before}
      {m.content ? <MarkdownBody streaming={m.streaming} citations={m.citations} onOpenCitation={onOpenCitation} renderWidget={m.renderWidget}>{m.content}</MarkdownBody> : m.streaming && !m.before ? <StreamingCaret active /> : null}
      {m.citations && m.citations.length > 0 ? <Sources citations={m.citations} onOpen={onOpenCitation} /> : null}
      {m.after}
    </>
  );
  if (m.bare) return <div className="min-w-0">{body}</div>;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
      <Message from={m.role}>
        <MessageContent from={m.role}>
          <MessageHeader name={m.author ?? assistantName} icon={assistantIcon} />
          {body}
          {m.suggestions && m.suggestions.length > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {m.suggestions.map((s) => (
                <li key={s.id} className={cn("flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-xs", s.applied ? "border-success-200 bg-success-50/40" : "border-sand-200")}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate"><span className="font-medium">{s.label}</span>: <span className="text-aeblack-900">{s.value}</span></p>
                    {s.detail ? <p className="truncate text-[11px] text-muted-foreground" title={s.detail}>{s.detail}</p> : null}
                  </div>
                  {s.confidence !== undefined && s.confidence !== null ? <span className="text-[10px] tabular text-muted-foreground">{Math.round(s.confidence * 100)}%</span> : null}
                  {s.applied ? (
                    <span className="inline-flex items-center gap-1">
                      <Badge size="sm" variant="success"><CheckCircle2 /> {labels.applied ?? microcopy("applied", locale)}</Badge>
                      {onUndoSuggestion ? <Button size="icon-sm" variant="ghost" className="size-6" title={labels.undo ?? microcopy("undo", locale)} aria-label={labels.undo ?? microcopy("undo", locale)} onClick={() => onUndoSuggestion(m.id, s)}><Undo2 className="size-3" /></Button> : null}
                    </span>
                  ) : onApplySuggestion ? <Button size="sm" variant="outline" className="h-7" onClick={() => onApplySuggestion(m.id, s)}>{labels.apply ?? microcopy("apply", locale)}</Button> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {(m.confidence !== undefined && m.confidence !== null) || m.escalatable ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-sand-200 pt-2">
              {m.confidence !== undefined && m.confidence !== null ? <ConfidenceMeter value={m.confidence} /> : <span />}
              {m.escalatable && onEscalate ? <Button size="sm" variant="outline" onClick={onEscalate}><Gavel /> {labels.escalate}</Button> : null}
            </div>
          ) : null}
        </MessageContent>
      </Message>
    </motion.div>
  );
}
